import type { TransferParameterTypeV4 } from './types';
import { generateCustomExtension } from '../../../api';
import type { GenerateCustomExtensionParams, ExtensionLogger } from '@sap/ux-specification-types';
import { CustomExtensionType, PropertyName, LogSeverity } from '@sap/ux-specification-types';
import type * as fpmWriter from '@sap-ux/fe-fpm-writer';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import i18next from 'i18next';
import { log } from '../../../extensionLogger';
import { extractLastIdPart } from './utils';

/**
 * Function creates a new custom column with aid of the FPM writer.
 *
 * @param propertyKey - the key of the property to be processed
 * @param transferParameters - list of parameters passed from API
 * @param configObject - current object in the configuration
 * @param logger - logger instance for logging messages
 * @param breadcrumbs - array of sequence of properties that we are currently processing.
 * @returns {boolean} - true if a new column was created
 */
export function createNewCustomColumn(
    propertyKey: string,
    transferParameters: TransferParameterTypeV4,
    configObject: { [key: string]: any },
    logger: ExtensionLogger,
    breadcrumbs: string[]
): boolean {
    if (!configObject) {
        return false;
    }
    if (!transferParameters.appRootPath) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOBASEPATH', { appId: transferParameters.appId })
        });
        return false;
    }
    if (configObject.template && configObject.template.indexOf(propertyKey) === -1) {
        //Existing fragment which does not fit to FPM writer syntax
        return false;
    }
    const sectionsIndex = breadcrumbs.lastIndexOf(PropertyName.sections);
    const sectionId = sectionsIndex > -1 ? extractLastIdPart(breadcrumbs[sectionsIndex + 1]) : undefined;
    const entityInManifest = sectionId ? sectionId.replace(/::/g, '/') : `@${UIAnnotationTerms.LineItem}`;
    const handler = configObject.eventHandler === true ? true : undefined;

    const data: fpmWriter.CustomTableColumn = {
        target: transferParameters.pageNameArray[0],
        targetEntity: entityInManifest,
        position: configObject.position,
        name: propertyKey,
        ...(configObject.header && { header: configObject.header }),
        ...(configObject.width && { width: configObject.width }),
        ...(configObject.availability && { availability: configObject.availability }),
        ...(handler && { eventHandler: handler })
    };
    const columnApiParams: GenerateCustomExtensionParams = {
        customExtension: CustomExtensionType.CustomColumn,
        basePath: transferParameters.appRootPath,
        data
    };
    generateCustomExtension(columnApiParams);
    return true;
}
